"use client"

import { useState } from 'react'
import Link from 'next/link'
import { Comperia } from 'lib/comperia/Comperia'
import { ExcerptMini } from './ExcerptMini'
import Carq from 'lib/carq/component/Carq'
import MoreButton from '@components/display/MoreButton'
import createSlug from '@utils/slug/createSlug'
import CAT from '@constants/categories'
import { PROPER_NAME_DELIMITER } from '../../../../../next-spl-part-utils/constants/delimiters'
import { slugifyCategory } from '../../../../../next-spl-part-utils/utils/partSlugUtils'
import slugifyUrl from '@utils/slug/slugifyUrl'
import { PART_LINK_BASE } from '@constants/searchFormLinks'
import mapSpecValue from './maps/specifications'
import useMediaQueryBreakpoint from '@hooks/useMediaQueryBreakpoint'
import useT from '@hooks/useTranslation'

import styles from 'styles/OfferSpecifications.module.css'

const LINK_PROP = {
	Akryl: 'lakier-akrylowy',
	Perłowy: 'lakier-perlowy',
	Matowy: 'lakier-matowy',
	Metalik: 'lakier-metalik',
	manual: 'skrzynia-biegow-manualna',
	Manualna: 'skrzynia-biegow-manualna',
	automatic: 'skrzynia-biegow-automatyczna',
	Automatyczna: 'skrzynia-biegow-automatyczna',
	'4by4': 'naped-4x4',
	'4x4': 'naped-4x4',
	front: 'naped-przednie-kola',
	'Na przednie koła': 'naped-przednie-kola',
	back: 'naped-tylne-kola',
	'Na Tylne Koła': 'naped-tylne-kola',
	'Na tylne koła': 'naped-tylne-kola',
}

const Specifications = ({
	buy = false,
	linkBase = {},
	offer = {},
	rent = false,
}) => {
	const [showSecondary, setShowSecondary] = useState(false)
	const onMobile = useMediaQueryBreakpoint(800)
	const t = {
		info: useT('Podstawowe informacje'),
	}

	if (!offer) return null

	const type = offer.type || ''
	const akol = offer.akol || {}
	const data = offer.data || {}
	const moreinfo = offer.moreinfo || {}
	const price = offer.price || {}

	const doLink = (components, params = '') =>
		`${linkBase}/${components
			.filter(Boolean)
			.map((v) => encodeURIComponent(v))
			.join('/')}` + params

	const isAgri = type === 'agri_ad'
	const isConstruction = type === 'budowlane'
	const isHeavy = type === 'ciezarowe'
	const isTool = type === 'tool_ad'
	const isTrailer = type === 'przyczepy'
	const isMotorcycle = type === 'motorcycle_ad'
	const isService = type === 'service_ad'
	const isPart = type === 'part_ad'
	const isBuy = buy
	const isRent = rent || Boolean(offer?.price?.rent)
    const isVehicle = Boolean(offer.make)

	const makeLink = rent ? linkBase : doLink([offer.make])
	const modelLink = rent
		? linkBase
		: doLink([offer.make, offer.model])

	const bodyType = data.body_type || data.truck_body_type
	const state =
		typeof offer._new === 'boolean'
			? offer._new
			: (offer.part_state || '').toLowerCase().startsWith('n')

	// const offerModel = offer.make && offer.model && `${offer.make} ${offer.model}`

	const primarySpec = [
		...(isPart
			? [
					[
						'Kategoria części',
						partCat(offer.part_category, offer.part_vehicle),
					],
					[
						'Stan',
						state ? 'Nowe' : 'Używane',
						null,
						null, // isBuy ? null : `${makeLink}?stan=${offer.part_state}`,
					],
					[
						'Pojazd',
						offer.part_vehicle,
						null,
						null, // `${makeLink}?pojazd=${offer.part_vehicle}`,
					],
					[
						'Producent części',
						offer.part_producer,
						null,
						null, // `${makeLink}?pojazd=${offer.part_producer}`,
					],
					[
						'Numer części',
						offer.part_reference_number,
						null,
						null, // `${makeLink}?numer-referencyjny=${offer.part_reference_number}`,
					],
					['Oferta firmowa', Boolean(data.dealer)],
					[
						'Cale',
						data.inches,
						null,
						null, // `${makeLink}?cale=${data.inches}`,
					],
					[
						'Profil',
						data.profile,
						null,
						null, // `${makeLink}?profil=${data.profile}`,
					],
					[
						'Szerokość opony',
						data.width,
						null,
						null, // `${makeLink}?szerokosc-opony=${data.width}`,
					],
					[
						'Opór toczenia',
						data.friction,
						null,
						null, // `${makeLink}?opor-toczenia=${data.friction}`,
					],
					[
						'Poziom głośności',
						data.loudness,
						null,
						null, // `${makeLink}?poziom-glosnosci=${data.loudness}`,
					],
					[
						'Indeks prędkości',
						data.speed_index,
						null,
						null, // `${makeLink}?indeks-predkosci=${data.speed_index}`,
					],
					[
						'Przyczepność na mokrej powierzchni',
						data.adhesiveness_index,
						null,
						null, // `${makeLink}?przyczepnosc=${data.adhesiveness_index}`,
					],
					partDelivery(offer),
			  ]
			: []),
		// ['Rodzaj oferty',                   offerKind(data)],
		// ['Kategoria oferty',                   CAT[offer.type],                         null,   `${makeLink}?kategoria=${partCategorySlug}`],
		[
			'Kategoria oferty',
			isPart ? null : CAT[offer._adtype || offer.type],
			null,
			isBuy || isRent ? null : linkBase,
		],

		['Marka', offer.make, null, isRent ? null : makeLink],
		['Model', offer.model, null, isRent ? null : modelLink],
		['Kategoria', offer.category],
		['Podkategoria', offer.subcategory],
		[
			'Rodzaj',
			offer.kind,
			null,
			isAgri ? `${makeLink}?rodzaj=${offer.kind}` : null,
		],

        ...(isVehicle
            ? [
                ['Bezwypadkowy',                       data.noaccident],
                ['Pierwszy właściciel',                data.first_owner],
                ['Historia serwisu w ASO',             data.ASO_history],
                ['Zarejestrowany jako zabytek',        data.relic],
                ['Homologacja ciężarowa',              data.truck_approval],
                ['Poleasingowy',                       data.after_leasing],
                ['Uszkodzony',                         data.damaged],
            ]
            : []
        )
	]

	const specShiftingOnMobile = [
		[
			'Generacja',
			offer.generation != '-' ? offer.generation : null,
			null,
			isAgri || isConstruction || isRent || isTrailer
				? null
				: `${modelLink}?generacja=${encodeURIComponent(
						offer.generation,
				  )}`,
		],
		['Wersja', data.equipment],
		['Rok produkcji', offer.production_year],
		[
			'Stan',
			isService || isPart ? null : state ? 'Nowe' : 'Używane',
			null,
			isAgri ||
			isBuy ||
			isConstruction ||
			isRent ||
			isTool ||
			isTrailer
				? null
				: `${makeLink}?stan=${state ? 'Nowy' : 'Uzywany'}`,
		],

		[
			'Dopuszczalna ładowność',
			data.load_capacity,
			'kg',
			isAgri || isConstruction || isHeavy || isRent || isTrailer
				? null
				: `${makeLink}?dopuszczalna-ladownosc-od=${data.load_capacity}`,
		],
		[
			'Dopuszczalna masa całkowita',
			data.total_mass,
			'kg',
			isAgri || isConstruction || isHeavy || isRent || isTrailer
				? null
				: `${makeLink}?dopuszczalna-masa-od=${data.total_mass}`,
		],
		[
			'Kubatura',
			data.capacity,
			'm³',
			isAgri || isConstruction || isHeavy || isRent || isTrailer
				? null
				: `${makeLink}?kubatura-od=${data.capacity}`,
		],

		[
			'Ogłoszenie autoryzowanego dealera',
			data.authorized,
			null,
			isAgri || isConstruction || isRent || isTrailer
				? null
				: `${makeLink}?autoryzowany-dealer=true`,
		],
		['Oferta firmowa', isPart ? null : data.dealer],
		['Oferta prywatna', isPart ? null : data.private],
		[
			'Podkategoria pojazdu',
			akol.podkategoria_pojazdu,
			null,
			isAgri || isConstruction || isRent || isTrailer
				? null
				: `${makeLink}?podkategoria-pojazdu=${akol.podkategoria_pojazdu}`,
		],
		[
			'Napęd',
			akol.naped,
			null,
			isAgri ||
			isBuy ||
			isConstruction ||
			isHeavy ||
			isRent ||
			isTrailer
				? null
				: `${makeLink}?naped=${akol.naped}`,
		],
		[
			'Wysięgnik',
			akol.wysiegnik,
			null,
			isAgri || isConstruction || isRent || isTrailer
				? null
				: `${makeLink}?wysiegnik=${akol.wysiegnik}`,
		],
		[
			'Lemiesz',
			akol.lemiesz,
			null,
			isAgri || isConstruction || isRent || isTrailer
				? null
				: `${makeLink}?lemiesz=${akol.lemiesz}`,
		],
		['Rok modelowy', moreinfo.model_year],
		[
			'Typ',
			bodyType,
			null,
			isAgri || isBuy || isConstruction || isRent || isTrailer
				? null
				: `${makeLink}?${
						isMotorcycle ? 'typ' : 'typ-nadwozia'
				  }=${bodyType}`,
		],
		['Miejsce odbioru', offer.rent?.reception_city],
		['Silnik', offer.trim],
		['Pojemność silnika', data.displacement_capacity, 'cm³'],
		['Moc silnika', data.engine_power, 'KM'],
		[
			'Skrzynia biegów',
			data.gearbox,
			null,
			isAgri ||
			isBuy ||
			isConstruction ||
			isHeavy ||
			isMotorcycle ||
			isRent ||
			isTrailer
				? null
				: LINK_PROP[data.gearbox]
				? `${makeLink}?${LINK_PROP[data.gearbox]}=true`
				: makeLink,
		],
		['Motogodziny', offer.motohours, 'h'],
		[
			'Napęd',
			data.drive,
			null,
			isAgri ||
			isBuy ||
			isConstruction ||
			isHeavy ||
			isRent ||
			isTrailer
				? null
				: LINK_PROP[data.drive]
				? `${makeLink}?${LINK_PROP[data.drive]}=true`
				: null,
		],
		['Rodzaj napędu', data.drive_4by4_type],
		['Klimatyzacja', data.air_conditioning],
		['Rodzaj klimatyzacji', data.automatic_air_conditioning_type],
		[
			'Kolor',
			offer.color,
			null,
			isAgri ||
			isConstruction ||
			isHeavy ||
			isMotorcycle ||
			isRent ||
			isTrailer
				? null
				: `${makeLink}?kolor=kolor-${createSlug(offer.color)}`,
		],
		['Rodzaj lakieru', data.lacquer],
		[
			'Możliwość finansowania',
			data.financing,
			null,
			isAgri || isConstruction || isRent || isTrailer
				? null
				: `${makeLink}?finansowanie=true`,
		],
		['Faktura VAT', offer.price?.price_invoice],
		['Status sprowadzonego', akol.status_sprowadzonego],
		[
			'Kraj pochodzenia',
			data.country_of_origin,
			null,
			isAgri ||
			isConstruction ||
			isHeavy ||
			isMotorcycle ||
			isRent ||
			isTrailer
				? null
				: `${makeLink}?kraj-pochodzenia=${data.country_of_origin}`,
		],
		['Kraj rejestracji', data.registered_in],
		// ['Data pierwszej rejestracji',         simpleDate(moreinfo.first_registration_date)],
		// ['Numer rejestracyjny',                data.registration_number],
		// ['VIN',                                offer.vin],
	]

	const secondarySpec = [
		['Liczba drzwi', data.doors],
		['Liczba miejsc', data.seats],
		['Audio', akol.audio],
		[
			'Kierownica po prawej stronie',
			data.steeringwheel,
			null,
			isAgri || isConstruction || isHeavy || isRent || isTrailer
				? null
				: `${makeLink}?kierownica-po-prawej=true`,
		],
		['Szerokość lemiesza', akol.szerokosc_lemiesza_w_m, 'm'],
		['Liczba silników', akol.liczba_silnikow],
		['Długość', akol.dlugosc_w_cm, 'm'],
		['Szerokość', akol.szerokosc_w_cm, 'm'],
		['Liczba osi', akol.liczba_osi],
		['Liczba łopat silnika', akol.liczba_lopat_silnika],
		['Wysokość podnoszenia', akol.wysokosc_podnoszenia_w_cm, 'cm'],
		['Waga', akol.waga_w_tonach, 'T'],
		['Udźwig', akol.udzwig_w_kg, 'kg'],
		['Motogodziny do przeglądu', akol.motogodziny_do_przegladu, 'h'],
		['Pojemność łyżki', akol.pojemnosc_lyzki_w_m3, 'm³'],
		['Masa eksploatacyjna', akol.masa_eksploatacyjna_w_tonach, 'T'],
		['Moc użyteczna', akol.moc_uzyteczna_w_KM, 'KM'],
		['Szerokość gąsienic', akol.szerokosc_gasienic_w_mm, 'mm'],
		['Głębokość kopania', akol.glebokosc_kopania_w_m, 'm'],
		['Moc agregatu', akol.moc_agregatu_w_kVA, 'kVA'],
		[
			'Zabytkowy',
			data.relic,
			null,
			isAgri ||
			isConstruction ||
			isHeavy ||
			isMotorcycle ||
			isRent ||
			isTrailer
				? null
				: `${makeLink}?zabytkowe=true`,
		],
		['Paliwo E10', akol.paliwo_e10],
		['Biodiesel', akol.biodiesel],
		['Olej roślinny', akol.olej_roslinny],
		['AdBlue', data.adblue],
		['Filtr cząstek stałych', data.particlefilter],
		['Pojazd nienormatywny', akol.pojazd_nienormatywny],

		['Cesja', data.transfer],
		['Cena do negocjacji', price.price_is_negotiable],
		['VAT marża', data.vatmargin],
		['Podwójne tylne koła', data.double_wheels],
		['Agregat chłodzący', data.cooling_aggregate],
		['Emisja CO2', data.co2_emission, 'g/km'],

		// ['Data dodania',                       simpleDate(offer.created)],
	]

	const usablePrimarySpec = onMobile
		? primarySpec
		: [...primarySpec, ...specShiftingOnMobile]

	const usableSecondarySpec = onMobile
		? [...specShiftingOnMobile, ...secondarySpec]
		: secondarySpec

	const viewableSecondarySpec = usableSecondarySpec.filter(
		([, value]) => value != null,
	)

	const hasSecondarySpec = viewableSecondarySpec.length > 0
	const showSecondarySpec = () => setShowSecondary(true)

	const hasVin = Boolean(offer.vin)

	return (
		<div className={styles.specs}>
			<h2>{t.info}</h2>
			<div>
				<List items={usablePrimarySpec} />
			</div>
			{hasVin && (
				<div className={styles.spec}>
					<span className={styles.name}>VIN</span>
					<span className={styles.value}>
						<ExcerptMini text={offer.vin} />
					</span>
				</div>
			)}
			{hasSecondarySpec && (
				<div>
					{onMobile || showSecondary ? (
						<List items={viewableSecondarySpec} />
					) : (
						<div style={{ textAlign: 'center' }}>
							<MoreButton onClick={showSecondarySpec} />
						</div>
					)}
				</div>
			)}
		</div>
	)
}

function VehicleHistory({ offer }) {
	const { _id, make, model } = offer || {}
	const isValidOffer = [_id, make, model].every(Boolean)
	if (!isValidOffer) return null

	return (
		<>
			<br/>
			<Comperia offer={offer} />
			<Carq key={_id} offer={offer} />
		</>
	)
}

function List({ items }) {
	return items.map((item, i) => <Spec spec={item} key={`key_${i}`} />)
}

function Spec({ spec }) {
	let [name, value, unit, link] = spec

	const t = {
		tak: useT('Tak'),
		nie: useT('Nie'),
		name: useT(name),
		value: useT(value),
	}

	if (value == null || value == '') return null

	value = mapSpecValue(name, value)
	const tName = t.name
	const tValue =
		typeof value === 'boolean'
			? value
				? t.tak
				: t.nie
			: isNaN(value)
			? t.value
			: value

	const valueComponent = (
		<>
			{tValue} {unit}
		</>
	)
	return (
        <div className={styles.spec}>
			<span className={styles.name}>{tName}</span>
			<span className={styles.value}>
				{link ? (
					<Link href={slugifyUrl(decodeURIComponent(link))}>
						{valueComponent}
					</Link>
				) : (
					valueComponent
				)}
			</span>
		</div>
    );
}

function partDelivery(offer) {
	const { data } = offer || {}
	const { shipment, personal_pickup } = data || {}
	const options = [
		shipment && 'Możliwa wysyłka',
		personal_pickup && 'Odbiór osobisty',
	].filter(Boolean)

	return ['Dostawa', options.length ? options.join('/') : null]
}

function partCat(fullPath, vehicle) {
	return typeof fullPath === 'string'
		? partCatTree(fullPath, vehicle)
		: null
}

function partCatTree (path, vehicle) {
	const pieces = []
	const bits = path.split(PROPER_NAME_DELIMITER)
	bits.reduce((acc, curr) => {
		acc.push(curr)
		const nextHref = PART_LINK_BASE + '?kategoria=' + slugifyCategory(acc.join(PROPER_NAME_DELIMITER)) + (vehicle ? '&pojazd=' + vehicle : '')
		pieces.push(<Link className={styles.path} href={nextHref} key={curr}>{curr}</Link>)
		return acc
	}, [])
	return pieces
}

Specifications.VehicleHistory = VehicleHistory

export default Specifications
export {
	VehicleHistory,
}